import { movies as rawMovies } from "@/mocks/movies";
import type { Movie } from "@/types/movie";

const allMovies = rawMovies as unknown as Movie[];

export const REGIONS = ["全部", "大陆", "港台", "美国", "日本", "韩国", "欧洲"] as const;
export const GENRES = [
  "全部",
  "科幻",
  "悬疑",
  "动作",
  "剧情",
  "爱情",
  "冒险",
  "喜剧",
  "动画",
  "战争",
  "古装",
  "犯罪",
  "奇幻",
] as const;
export const SORTS = [
  { value: "latest", label: "最新上映" },
  { value: "hot", label: "最多播放" },
  { value: "rating", label: "评分最高" },
] as const;

export function getAllMovies(): Movie[] {
  return allMovies;
}

export function getMovieById(id: string): Movie | undefined {
  return allMovies.find((m) => m.id === id);
}

export function getByType(type: string): Movie[] {
  if (type === "movie" || type === "tv") {
    return allMovies.filter((m) => m.type === type);
  }
  return [...allMovies];
}

export function getFeatured(): Movie[] {
  return allMovies.filter((m) => m.featured);
}

export function getLatest(limit = 8): Movie[] {
  return [...allMovies]
    .sort((a, b) => (a.updatedAt < b.updatedAt ? 1 : -1))
    .slice(0, limit);
}

export function getHot(limit = 8): Movie[] {
  return [...allMovies].sort((a, b) => b.views - a.views).slice(0, limit);
}

export function getTopRated(limit = 8): Movie[] {
  return [...allMovies].sort((a, b) => b.rating - a.rating).slice(0, limit);
}

export interface FilterState {
  region: string;
  genre: string;
  year: string;
  sort: string;
}

export const DEFAULT_FILTERS: FilterState = {
  region: "全部",
  genre: "全部",
  year: "全部",
  sort: "latest",
};

export function getYearOptions(): string[] {
  const years = Array.from(new Set(allMovies.map((m) => m.year))).sort((a, b) => b - a);
  return ["全部", ...years.map((y) => `${y}`), "更早"];
}

export function filterAndSort(base: Movie[], filters: FilterState): Movie[] {
  const { region, genre, year, sort } = filters;
  let result = base.filter((m) => {
    if (region !== "全部" && m.region !== region) return false;
    if (genre !== "全部" && !m.genres.includes(genre)) return false;
    if (year !== "全部") {
      if (year === "更早") {
        if (m.year > 2021) return false;
      } else if (`${m.year}` !== year) {
        return false;
      }
    }
    return true;
  });

  if (sort === "hot") {
    result = result.sort((a, b) => b.views - a.views);
  } else if (sort === "rating") {
    result = result.sort((a, b) => b.rating - a.rating);
  } else {
    result = result.sort((a, b) => (a.updatedAt < b.updatedAt ? 1 : -1));
  }
  return result;
}

export function searchMovies(keyword: string): Movie[] {
  const q = keyword.trim().toLowerCase();
  if (!q) return [];
  return allMovies.filter((m) => {
    const haystack = [m.title, m.director, ...m.genres, ...m.tags, ...m.actors]
      .join(" ")
      .toLowerCase();
    return haystack.includes(q);
  });
}

export function getRelated(movie: Movie, limit = 6): Movie[] {
  const scored = allMovies
    .filter((m) => m.id !== movie.id)
    .map((m) => {
      let score = 0;
      if (m.type === movie.type) score += 1;
      score += m.genres.filter((g) => movie.genres.includes(g)).length * 3;
      score += m.tags.filter((t) => movie.tags.includes(t)).length;
      return { m, score };
    })
    .filter((x) => x.score > 0)
    .sort((a, b) => b.score - a.score);
  return scored.slice(0, limit).map((x) => x.m);
}

export function formatViews(views: number): string {
  if (views >= 10000) return `${(views / 10000).toFixed(1)}万`;
  return `${views}`;
}

export const TYPE_LABEL: Record<string, string> = {
  movie: "电影",
  tv: "电视剧",
};

export const TYPE_DESC: Record<string, string> = {
  movie: "汇聚院线热映、口碑佳片与经典之作，按类型、地区、年份快速找到你想看的那一部。",
  tv: "从古装权谋到都市爱情，热门连载剧集一网打尽，支持按地区与类型精准筛选。",
};